import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Home, Camera, BookOpen, User as UserIcon } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// Routes where the bottom tab bar is suppressed. These are either
// pre-auth (no user), full-screen single-purpose flows (camera, AR,
// onboarding wizard), or admin/billing surfaces where the tab bar
// would distract from the task.
const HIDDEN_ROUTE_PATTERNS = [
  /^\/$/, // Welcome / marketing
  /^\/login/,
  /^\/auth\//,
  /^\/onboarding/,
  /^\/analyzing/,
  /^\/admin(\/|$)/,
  /^\/yc(\/|$)/,
  /^\/lock-in/,
  /^\/pride/,
  /^\/pitch/,
  /^\/pricing/,
  /^\/billing\//,
  /^\/dev\//,
];

// Each tab declares the route patterns it should highlight on. The
// most-specific match wins via array order — `Capture` consumes both
// /capture and /recreate (both are "start a new session" flows);
// `Library` covers all browse/saved/learn surfaces.
const TAB_DEFS = [
  {
    to: "/home",
    icon: Home,
    labelKey: "tabbar.home",
    labelFallback: "Home",
    testid: "mobile-tabbar-home",
    activeOn: [/^\/home$/],
  },
  {
    to: "/capture",
    icon: Camera,
    labelKey: "tabbar.capture",
    labelFallback: "Capture",
    testid: "mobile-tabbar-capture",
    activeOn: [/^\/capture/, /^\/recreate/],
  },
  {
    to: "/my-routines",
    icon: BookOpen,
    labelKey: "tabbar.library",
    labelFallback: "Library",
    testid: "mobile-tabbar-library",
    activeOn: [/^\/my-routines/, /^\/routine\//, /^\/techniques/, /^\/arsenal/],
  },
  {
    to: "/settings",
    icon: UserIcon,
    labelKey: "tabbar.profile",
    labelFallback: "Profile",
    testid: "mobile-tabbar-profile",
    activeOn: [/^\/settings/],
  },
];

const MobileTabBar = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { pathname } = useLocation();

  // Hidden when signed-out OR on a route that opts out (camera, admin,
  // welcome marketing, etc.). Desktop never sees this — Tailwind's
  // `sm:hidden` does that with zero JS cost.
  if (!user) return null;
  if (HIDDEN_ROUTE_PATTERNS.some((re) => re.test(pathname))) return null;

  return (
    <>
      {/* Spacer pushes document height up by the bar's height so users
          can scroll the last bit of page content into view past the
          floating bar. Only takes space on mobile. The `env(safe-area-
          inset-bottom)` part respects iPhone notch / home-indicator. */}
      <div
        className="sm:hidden"
        style={{ height: "calc(4.5rem + env(safe-area-inset-bottom, 0px))" }}
        aria-hidden="true"
        data-testid="mobile-tabbar-spacer"
      />
      <nav
        className="sm:hidden fixed bottom-0 left-0 right-0 z-40 glass-card border-t border-[#EADDD7]/60"
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
        data-testid="mobile-tab-bar"
        aria-label="Primary navigation"
      >
        <ul className="flex items-stretch justify-around px-1 py-1">
          {TAB_DEFS.map((tab) => {
            const Icon = tab.icon;
            const active = tab.activeOn.some((re) => re.test(pathname));
            return (
              <li key={tab.to} className="flex-1">
                <Link
                  to={tab.to}
                  className={`flex flex-col items-center justify-center gap-0.5 py-2 px-1 rounded-xl transition-colors ${
                    active
                      ? "text-[#9B6A6C]"
                      : "text-[#8A7D82] hover:text-[#5C4B51]"
                  }`}
                  aria-current={active ? "page" : undefined}
                  data-testid={tab.testid}
                >
                  <Icon
                    className={`h-5 w-5 ${active ? "stroke-[2.25]" : ""}`}
                    aria-hidden="true"
                  />
                  <span className="text-[11px] leading-none font-medium">
                    {t(tab.labelKey, tab.labelFallback)}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>
    </>
  );
};

export default MobileTabBar;
